package co.maytes.standupbot.dashboard.features.calendar

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.feedback.EmptyState
import co.maytes.standupbot.dashboard.components.feedback.SkeletonListRow
import co.maytes.standupbot.dashboard.components.motion.StateSwap
import co.maytes.standupbot.dashboard.components.widgets.Card
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideCalendar
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.calendar.CalendarEventDto
import co.maytes.standupbot.shared.dto.calendar.CalendarEventsResponseDto
import co.maytes.standupbot.shared.dto.integration.CalendarAvailability
import co.maytes.standupbot.shared.dto.integration.IntegrationDto
import co.maytes.standupbot.shared.dto.integration.IntegrationProviderDto
import co.maytes.standupbot.shared.dto.integration.calendarAvailability
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import org.jetbrains.compose.web.css.px

@Composable
fun TodaysCalendarCard(
    workspaceId: String?,
    timezoneId: String,
    title: String = "Today's calendar",
) {
    val api = rememberApiClient()
    val tz = remember(timezoneId) {
        runCatching { TimeZone.of(timezoneId) }.getOrDefault(TimeZone.currentSystemDefault())
    }
    val today = remember(tz) { Clock.System.todayIn(tz) }
    var loading by remember(workspaceId, timezoneId) { mutableStateOf(true) }
    var errored by remember(workspaceId, timezoneId) { mutableStateOf(false) }
    var availability by remember(workspaceId, timezoneId) { mutableStateOf(CalendarAvailability.AVAILABLE) }
    var fixPath by remember(workspaceId, timezoneId) { mutableStateOf(GOOGLE_FIX_PATH) }
    var events by remember(workspaceId, timezoneId) { mutableStateOf<List<CalendarEventDto>>(emptyList()) }

    LaunchedEffect(workspaceId, timezoneId, today) {
        if (workspaceId.isNullOrBlank()) {
            loading = false
            return@LaunchedEffect
        }
        loading = true
        errored = false
        availability = CalendarAvailability.AVAILABLE
        val google = (api.get<List<IntegrationDto>>("/api/v1/workspaces/$workspaceId/integrations") as? Result.Ok)
            ?.value
            ?.firstOrNull { it.provider == IntegrationProviderDto.GOOGLE_CALENDAR }
        availability = google.calendarAvailability()
        fixPath = google?.health?.fix?.path ?: GOOGLE_FIX_PATH
        if (availability != CalendarAvailability.AVAILABLE) {
            events = emptyList()
            loading = false
            return@LaunchedEffect
        }
        val res = api.get<CalendarEventsResponseDto>(
            "/api/v1/workspaces/$workspaceId/calendar/events?tz=$timezoneId&date=$today",
        )
        when (res) {
            is Result.Ok -> {
                events = res.value.items
                errored = false
            }
            is Result.Err -> {
                events = emptyList()
                errored = true
            }
        }
        loading = false
    }

    Card(modifier = Modifier.fillMaxWidth()) {
        Column(modifier = Modifier.fillMaxWidth().gap(Spacing.lg)) {
            SpanText(
                text = title,
                modifier = Modifier
                    .fontSize(Typography.h3.size)
                    .fontWeight(Typography.h3.weight)
                    .color(Palette.textPrimary),
            )
            StateSwap(state = CalendarPhase.of(loading, availability, errored, events.isNotEmpty())) {
                when {
                    loading -> Column(modifier = Modifier.fillMaxWidth()) {
                        repeat(3) { SkeletonListRow() }
                    }
                    availability == CalendarAvailability.UNHEALTHY -> CalendarProblemRow(
                        message = "Calendar disconnected: ",
                        linkText = "reconnect Google in Settings →",
                        path = fixPath,
                    )
                    availability == CalendarAvailability.NOT_CONNECTED -> CalendarProblemRow(
                        message = "Calendar not connected: ",
                        linkText = "connect Google in Settings →",
                        path = fixPath,
                    )
                    errored -> Row(
                        modifier = Modifier.fillMaxWidth().gap(Spacing.xs),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        SpanText(
                            text = "Couldn't load calendar — ",
                            modifier = Modifier
                                .fontSize(Typography.bodySmall.size)
                                .color(Palette.textSecondary),
                        )
                        Link(
                            path = "/app/settings/integrations",
                            text = "connect Google in Settings →",
                            modifier = Modifier
                                .fontSize(Typography.bodySmall.size)
                                .color(Palette.accent)
                                .fontWeight(500),
                        )
                    }
                    events.isEmpty() -> EmptyState(
                        title = "No events today",
                        subtitle = "Nothing on the calendar for today — a clear runway for focused work.",
                        icon = { LucideCalendar(size = 26) },
                    )
                    else -> Column(modifier = Modifier.fillMaxWidth().gap(Spacing.sm)) {
                        events.forEach { ev -> CalendarEventRow(ev) }
                    }
                }
            }
        }
    }
}

private const val GOOGLE_FIX_PATH = "/app/settings/integrations?fix=google"

@Composable
private fun CalendarProblemRow(
    message: String,
    linkText: String,
    path: String,
) {
    Row(
        modifier = Modifier.fillMaxWidth().gap(Spacing.xs),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            text = message,
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .color(Palette.textSecondary),
        )
        Link(
            path = path,
            text = linkText,
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .color(Palette.accent)
                .fontWeight(500),
        )
    }
}

@Composable
private fun CalendarEventRow(event: CalendarEventDto) {
    Row(
        modifier = Modifier.fillMaxWidth().gap(Spacing.md),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier
                .gap(Spacing.md)
                .styleModifier {
                    property("flex", "1 1 auto")
                    property("min-width", "0")
                },
        ) {
            SpanText(
                text = formatEventTime(event),
                modifier = Modifier
                    .fontSize(Typography.bodySmall.size)
                    .fontWeight(500)
                    .color(Palette.textSecondary)
                    .width(72.px)
                    .styleModifier {
                        property("flex-shrink", "0")
                        property("font-family", Typography.FONT_SANS); property("font-variant-numeric", "tabular-nums")
                    },
            )
            SpanText(
                text = event.title,
                modifier = Modifier
                    .fontSize(Typography.bodySmall.size)
                    .color(Palette.textPrimary)
                    .styleModifier {
                        property("min-width", "0")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("white-space", "nowrap")
                    },
            )
        }
        event.location?.takeIf { it.isNotBlank() }?.let { location ->
            SpanText(
                text = location,
                modifier = Modifier
                    .fontSize(Typography.caption.size)
                    .color(Palette.textTertiary)
                    .styleModifier {
                        property("flex-shrink", "0")
                        property("max-width", "40%")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("white-space", "nowrap")
                    },
            )
        }
    }
}

private fun formatEventTime(event: CalendarEventDto): String {
    if (event.allDay) return "All day"
    val raw = event.startIso
    val t = raw.substringAfter('T', "").take(5)
    return if (t.length == 5) t else raw
}
